import './styles/tokens.css';
import './styles/base.css';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router/dom';
import { router } from './app/router.tsx';
import { createQueryClient } from './lib/query.ts';

const queryClient = createQueryClient();
const container = document.getElementById('root');
if (!container) throw new Error('#root element missing from index.html');

// iOS Safari ignores user-scalable=no; its own pinch gesture events are blocked here (scrolling still works).
for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
  document.addEventListener(type, (event) => event.preventDefault(), { passive: false });
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);

/**
 * After a new build, a tab that is still running the previous one asks for chunk files that no
 * longer exist, which leaves a blank page until a hard refresh. Reload once — guarded by a session
 * flag so a genuinely broken build can never loop.
 */
function reloadOnceForNewBuild(reason: string): void {
  try {
    if (sessionStorage.getItem('natashu:reloaded') === '1') return;
    sessionStorage.setItem('natashu:reloaded', '1');
  } catch {
    // Private mode without storage: reloading once per event is still better than a blank page.
  }
  console.warn(`[natashu] reloading for a new build (${reason})`);
  window.location.reload();
}

window.addEventListener('vite:preloadError', () => reloadOnceForNewBuild('chunk missing'));
window.addEventListener('error', (e) => {
  const msg = String((e as ErrorEvent).message ?? '');
  if (/dynamically imported module|Importing a module script failed|error loading dynamically/i.test(msg)) reloadOnceForNewBuild('module load failed');
});
window.addEventListener('load', () => {
  // The app started, so the current build works; allow a future reload.
  try {
    sessionStorage.removeItem('natashu:reloaded');
  } catch {
    // ignore
  }
});

/**
 * A tab left open keeps running the build it loaded (owner saw old layouts after deploys, v1.32.0). Every 5 minutes
 * and whenever the tab comes back into view, compare the entry script the server now serves with the running one.
 * New build: reload at once when coming back and nothing is being typed, otherwise offer a "โหลดใหม่" bar.
 */
function watchForNewBuild(): void {
  const current = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/"]')?.getAttribute('src');
  if (!current) return;
  let offered = false;
  const typing = () => {
    const el = document.activeElement as HTMLInputElement | HTMLTextAreaElement | null;
    return Boolean(el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable) && 'value' in el && el.value);
  };
  const offer = () => {
    if (offered) return;
    offered = true;
    const bar = document.createElement('button');
    bar.type = 'button';
    bar.className = 'new-build-bar';
    bar.textContent = 'มีเวอร์ชันใหม่ของระบบ — กดเพื่อโหลดใหม่';
    bar.addEventListener('click', () => window.location.reload());
    document.body.appendChild(bar);
  };
  const check = async (returning: boolean) => {
    try {
      const html = await (await fetch('/', { cache: 'no-store', credentials: 'same-origin' })).text();
      const served = /<script[^>]+type="module"[^>]+src="([^"]*\/assets\/[^"]+)"/.exec(html)?.[1];
      if (!served || served === current) return;
      if (returning && !typing()) window.location.reload();
      else offer();
    } catch {
      // Offline or server restarting: try again later.
    }
  };
  window.setInterval(() => void check(false), 5 * 60_000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check(true);
  });
}

if (import.meta.env.PROD) window.addEventListener('load', watchForNewBuild);

// Service worker only for production builds; dev must never serve stale modules.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        void reg.update();
        // A newer worker took over (new build): the page must reload to match its assets.
        navigator.serviceWorker.addEventListener('controllerchange', () => reloadOnceForNewBuild('service worker updated'));
      })
      .catch(() => undefined);
  });
}
